import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import cronstrue from "cronstrue";
import {
  ScheduleCreateBody,
  type Frequency,
  type MonthlyMode,
  type ScheduleType,
} from "@qlik-scheduler/shared";

import { ApiError } from "../api/client.js";
import {
  useApps,
  useConnections,
  useCreateSchedule,
} from "../api/queries.js";
import { Button } from "../components/Button.js";
import { ErrorAlert } from "../components/ErrorAlert.js";
import { Modal } from "../components/Modal.js";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

type FrequencyChoice =
  | "ONCE"
  | "DAILY"
  | "WEEKLY"
  | "MONTHLY"
  | "CUSTOM"
  | "CHAINED";

// Each frequency card shows a glyph + a short title + a tighter sub-label.
// Glyphs are unicode for now; swap to inline SVG in a later pass if we want
// them brand-matched to the portal icon set.
const FREQUENCY_CARDS: Array<{
  key: FrequencyChoice;
  title: string;
  subtitle: string;
  glyph: string;
}> = [
  { key: "ONCE", title: "Once", subtitle: "A single run at start time", glyph: "●" },
  { key: "DAILY", title: "Daily", subtitle: "Every day", glyph: "☀" },
  { key: "WEEKLY", title: "Weekly", subtitle: "Every week", glyph: "📅" },
  { key: "MONTHLY", title: "Monthly", subtitle: "Every month", glyph: "📆" },
  { key: "CUSTOM", title: "Custom", subtitle: "Cron expression", glyph: "⚙" },
  {
    key: "CHAINED",
    title: "Chained",
    subtitle: "After another reload",
    glyph: "⇢",
  },
];

// 0 = Sunday … 6 = Saturday (matches the schema). DOW chip row reads left-to-
// right as Mon → Sun but the underlying values stay 0-6.
const DOW_LABELS: Array<{ value: number; label: string }> = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

const INTRADAY_INTERVAL_OPTIONS = [5, 10, 15, 20, 30, 60];

const NTH_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, label: "1st" },
  { value: 2, label: "2nd" },
  { value: 3, label: "3rd" },
  { value: 4, label: "4th" },
  { value: 5, label: "5th" },
  { value: -1, label: "Last" },
];

// A pragmatic short list of common IANA zones. Browsers that support
// Intl.supportedValuesOf supply the full list — we use it when available.
const FALLBACK_TIMEZONES = [
  "UTC",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Europe/Stockholm",
  "Europe/Zurich",
  "Europe/Athens",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Seoul",
  "Asia/Bangkok",
  "Australia/Sydney",
  "Australia/Perth",
  "Pacific/Auckland",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "America/Mexico_City",
  "America/Argentina/Buenos_Aires",
];

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function allTimezones(): string[] {
  // Intl.supportedValuesOf is widely supported in modern browsers but typed as
  // optional. Fall back to the curated list.
  const intl = Intl as unknown as {
    supportedValuesOf?: (key: string) => string[];
  };
  if (typeof intl.supportedValuesOf === "function") {
    try {
      const all = intl.supportedValuesOf("timeZone");
      if (Array.isArray(all) && all.length > 0) return all;
    } catch {
      /* fall through */
    }
  }
  return FALLBACK_TIMEZONES;
}

type DailyRunMode = "ONCE_A_DAY" | "REPEAT";

// Local form shape. zod resolver validates the API body shape; some fields are
// derived (frequencyChoice → ScheduleType + Frequency + cronExpression).
type FormValues = {
  scheduleName: string;
  connectionId: number | null;
  appId: string;
  appName: string;
  frequencyChoice: FrequencyChoice;
  cronExpression: string;
  startTime: string; // datetime-local
  endDate: string;
  timezone: string;
  partialReload: boolean;
  retryEnabled: boolean;
  maxRetryAttempts: number;
  retryDelayMinutes: number;
  exponentialBackoff: boolean;
  apiKey: string;
  // --- Advanced scheduling (DAILY / WEEKLY / MONTHLY) ---------------------
  weeklyDaysOfWeek: number[]; // empty = every day
  dailyRunMode: DailyRunMode;
  intradayIntervalMinutes: number; // only used when dailyRunMode=REPEAT
  intradayStartHour: number;
  intradayEndHour: number;
  monthlyMode: MonthlyMode;
  monthlyDayOfMonth: number;
  monthlyDaysBeforeEnd: number;
  monthlyWeekday: number;
  monthlyNth: number;
  // --- Chained --------------------------------------------------------------
  chainAfterConnectionId: number | null;
  chainAfterAppId: string;
  chainAfterAppName: string;
};

export function ScheduleCreateModal({ open, onClose, onCreated }: Props) {
  if (!open) return null;
  return <ScheduleCreateModalInner onClose={onClose} onCreated={onCreated} />;
}

function ScheduleCreateModalInner({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const connectionsQuery = useConnections();
  const create = useCreateSchedule();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showRetry, setShowRetry] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);

  const defaultStart = useMemo(
    () => toLocalDatetimeValue(new Date(Date.now() + 5 * 60_000)),
    [],
  );

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      scheduleName: "",
      connectionId: null,
      appId: "",
      appName: "",
      frequencyChoice: "DAILY",
      cronExpression: "",
      startTime: defaultStart,
      endDate: "",
      timezone: browserTimezone(),
      partialReload: false,
      retryEnabled: true,
      maxRetryAttempts: 3,
      retryDelayMinutes: 5,
      exponentialBackoff: false,
      apiKey: "",
      weeklyDaysOfWeek: [],
      dailyRunMode: "ONCE_A_DAY",
      intradayIntervalMinutes: 15,
      intradayStartHour: 6,
      intradayEndHour: 17,
      monthlyMode: "DAY_OF_MONTH",
      monthlyDayOfMonth: 1,
      monthlyDaysBeforeEnd: 0,
      monthlyWeekday: 1,
      monthlyNth: 1,
      chainAfterConnectionId: null,
      chainAfterAppId: "",
      chainAfterAppName: "",
    },
  });

  const connectionId = useWatch({ control, name: "connectionId" });
  const frequencyChoice = useWatch({ control, name: "frequencyChoice" });
  const cronValue = useWatch({ control, name: "cronExpression" });
  const startTimeValue = useWatch({ control, name: "startTime" });
  const weeklyDaysOfWeek = useWatch({ control, name: "weeklyDaysOfWeek" });
  const dailyRunMode = useWatch({ control, name: "dailyRunMode" });
  const monthlyMode = useWatch({ control, name: "monthlyMode" });
  const chainAfterConnectionId = useWatch({
    control,
    name: "chainAfterConnectionId",
  });
  const chainAfterAppName = useWatch({ control, name: "chainAfterAppName" });

  // App list for the selected connection (the "target" app — the one being
  // scheduled). Stays cached so re-opening the modal doesn't refetch from
  // scratch.
  const appsQuery = useApps(
    { connectionId: connectionId ?? -1, limit: 200, canReloadOnly: true },
    {
      enabled: connectionId != null,
      staleTime: 60_000,
    },
  );

  // App list for the *parent* connection (CHAINED only). We don't require
  // canReloadOnly here because the parent just needs to be reloadable enough
  // to emit a success signal — Qlik's reload API decides — but in practice
  // canReloadOnly mirrors what users want.
  const parentAppsQuery = useApps(
    { connectionId: chainAfterConnectionId ?? -1, limit: 200, canReloadOnly: true },
    {
      enabled: frequencyChoice === "CHAINED" && chainAfterConnectionId != null,
      staleTime: 60_000,
    },
  );

  // For WEEKLY, default the day-of-week to the day implied by startTime when
  // the user hasn't picked any. Re-runs whenever the start time changes.
  useEffect(() => {
    if (frequencyChoice !== "WEEKLY") return;
    if (weeklyDaysOfWeek && weeklyDaysOfWeek.length > 0) return;
    const d = new Date(startTimeValue);
    if (Number.isNaN(d.getTime())) return;
    setValue("weeklyDaysOfWeek", [d.getDay()], { shouldDirty: false });
    // We intentionally exclude weeklyDaysOfWeek so the user can clear without
    // it snapping back on every render — only re-default when the start time
    // shifts the natural choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequencyChoice, startTimeValue, setValue]);

  const cronPreview = useMemo(() => {
    if (frequencyChoice !== "CUSTOM") return null;
    const cron = cronValue?.trim();
    if (!cron) return null;
    try {
      return cronstrue.toString(cron, { use24HourTimeFormat: true });
    } catch (err) {
      return err instanceof Error ? `Invalid cron: ${err.message}` : "Invalid cron";
    }
  }, [frequencyChoice, cronValue]);

  async function onSubmit(values: FormValues) {
    setSubmitError(null);

    if (!values.connectionId) {
      setSubmitError("Select a connection.");
      return;
    }
    if (!values.appId) {
      setSubmitError("Select an app.");
      return;
    }

    // CHAINED — short-circuit before deriving cron / startTime stuff.
    if (values.frequencyChoice === "CHAINED") {
      if (
        !values.chainAfterConnectionId ||
        !values.chainAfterAppId ||
        !values.chainAfterAppName
      ) {
        setSubmitError("Pick the parent connection and app to chain from.");
        return;
      }
      if (
        values.chainAfterConnectionId === values.connectionId &&
        values.chainAfterAppId === values.appId
      ) {
        setSubmitError("A schedule cannot chain off its own app.");
        return;
      }
      const chainedBody = {
        scheduleName: values.scheduleName,
        connectionId: values.connectionId,
        appId: values.appId,
        appName: values.appName,
        scheduleType: "CHAINED" as ScheduleType,
        // startTime intentionally omitted — server accepts CHAINED schedules
        // without it (and defaults the unused DB column to now()).
        timezone: values.timezone || "UTC",
        partialReload: values.partialReload,
        retryEnabled: values.retryEnabled,
        maxRetryAttempts: values.maxRetryAttempts,
        retryDelayMinutes: values.retryDelayMinutes,
        exponentialBackoff: values.exponentialBackoff,
        notifyOnFailure: false,
        notifyEmails: [],
        chainAfterConnectionId: values.chainAfterConnectionId,
        chainAfterAppId: values.chainAfterAppId,
        chainAfterAppName: values.chainAfterAppName,
        ...(values.apiKey ? { apiKey: values.apiKey } : {}),
      };

      const parsed = ScheduleCreateBody.safeParse(chainedBody);
      if (!parsed.success) {
        setSubmitError(
          parsed.error.issues
            .map((i) => `${i.path.join(".")}: ${i.message}`)
            .join("; "),
        );
        return;
      }
      try {
        await create.mutateAsync(parsed.data);
        onCreated();
      } catch (err) {
        setSubmitError(describeError(err));
      }
      return;
    }

    const scheduleType: ScheduleType =
      values.frequencyChoice === "ONCE" ? "ONCE" : "RECURRING";
    let frequency: Frequency | undefined;
    let cronExpression: string | undefined;

    switch (values.frequencyChoice) {
      case "ONCE":
        frequency = undefined;
        cronExpression = undefined;
        break;
      case "DAILY":
        frequency = "DAILY";
        cronExpression = cronFromStart(values.startTime, "DAILY");
        break;
      case "WEEKLY":
        frequency = "WEEKLY";
        cronExpression = cronFromStart(values.startTime, "WEEKLY");
        break;
      case "MONTHLY":
        frequency = "MONTHLY";
        cronExpression = cronFromStart(values.startTime, "MONTHLY");
        break;
      case "CUSTOM":
        frequency = "CUSTOM";
        cronExpression = values.cronExpression.trim();
        if (!cronExpression) {
          setSubmitError("Provide a cron expression for Custom frequency.");
          return;
        }
        try {
          cronstrue.toString(cronExpression);
        } catch {
          setSubmitError("Cron expression is not valid.");
          return;
        }
        break;
    }

    const startIso = localDatetimeToIso(values.startTime);
    if (!startIso) {
      setSubmitError("Start time is required.");
      return;
    }
    const endIso = values.endDate ? localDatetimeToIso(values.endDate) : undefined;

    // --- Advanced field marshalling ----------------------------------------
    // WEEKLY must carry at least one DOW. DAILY may carry DOW (empty = every
    // day). MONTHLY/ONCE/CUSTOM ignore DOW.
    let weeklyDow: number[] | undefined;
    if (values.frequencyChoice === "WEEKLY") {
      if (!values.weeklyDaysOfWeek || values.weeklyDaysOfWeek.length === 0) {
        setSubmitError("Pick at least one day of the week.");
        return;
      }
      weeklyDow = [...new Set(values.weeklyDaysOfWeek)].sort();
    } else if (values.frequencyChoice === "DAILY") {
      weeklyDow =
        values.weeklyDaysOfWeek && values.weeklyDaysOfWeek.length > 0
          ? [...new Set(values.weeklyDaysOfWeek)].sort()
          : undefined;
    }

    // Intra-day repetition (DAILY + REPEAT only).
    let intradayInterval: number | undefined;
    let intradayStart: number | undefined;
    let intradayEnd: number | undefined;
    if (
      values.frequencyChoice === "DAILY" &&
      values.dailyRunMode === "REPEAT"
    ) {
      intradayInterval = values.intradayIntervalMinutes;
      intradayStart = values.intradayStartHour;
      intradayEnd = values.intradayEndHour;
      if (intradayStart > intradayEnd) {
        setSubmitError("Intra-day end hour must be greater than or equal to start hour.");
        return;
      }
    }

    // Monthly mode fields (MONTHLY only).
    let monthlyFields: {
      monthlyMode?: MonthlyMode;
      monthlyDayOfMonth?: number;
      monthlyDaysBeforeEnd?: number;
      monthlyWeekday?: number;
      monthlyNth?: number;
    } = {};
    if (values.frequencyChoice === "MONTHLY") {
      switch (values.monthlyMode) {
        case "DAY_OF_MONTH":
          monthlyFields = {
            monthlyMode: "DAY_OF_MONTH",
            monthlyDayOfMonth: values.monthlyDayOfMonth,
          };
          break;
        case "LAST_DAY":
          monthlyFields = { monthlyMode: "LAST_DAY" };
          break;
        case "DAYS_BEFORE_END":
          monthlyFields = {
            monthlyMode: "DAYS_BEFORE_END",
            monthlyDaysBeforeEnd: values.monthlyDaysBeforeEnd,
          };
          break;
        case "NTH_WEEKDAY":
          monthlyFields = {
            monthlyMode: "NTH_WEEKDAY",
            monthlyNth: values.monthlyNth,
            monthlyWeekday: values.monthlyWeekday,
          };
          break;
      }
    }

    const body = {
      scheduleName: values.scheduleName,
      connectionId: values.connectionId,
      appId: values.appId,
      appName: values.appName,
      scheduleType,
      frequency,
      cronExpression,
      startTime: startIso,
      endDate: endIso,
      timezone: values.timezone,
      partialReload: values.partialReload,
      retryEnabled: values.retryEnabled,
      maxRetryAttempts: values.maxRetryAttempts,
      retryDelayMinutes: values.retryDelayMinutes,
      exponentialBackoff: values.exponentialBackoff,
      notifyOnFailure: false,
      notifyEmails: [],
      ...(weeklyDow ? { weeklyDaysOfWeek: weeklyDow } : {}),
      ...(intradayInterval != null
        ? {
            intradayIntervalMinutes: intradayInterval,
            intradayStartHour: intradayStart,
            intradayEndHour: intradayEnd,
          }
        : {}),
      ...monthlyFields,
      ...(values.apiKey ? { apiKey: values.apiKey } : {}),
    };

    const parsed = ScheduleCreateBody.safeParse(body);
    if (!parsed.success) {
      setSubmitError(parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; "));
      return;
    }

    try {
      await create.mutateAsync(parsed.data);
      onCreated();
    } catch (err) {
      setSubmitError(describeError(err));
    }
  }

  const timezones = useMemo(allTimezones, []);
  const connections = (connectionsQuery.data ?? []).filter((c) => c.isActive);
  const appOptions = appsQuery.data?.data ?? [];
  const parentAppOptions = useMemo(
    () =>
      [...(parentAppsQuery.data?.data ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      ),
    [parentAppsQuery.data],
  );

  const isChained = frequencyChoice === "CHAINED";

  return (
    <Modal open onClose={onClose} title="Create schedule" size="lg">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
        {/* Section 1: What */}
        <Section title="What" subtitle="Pick the connection and app, and give the schedule a name.">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField label="Schedule name" htmlFor="sched-name" error={errors.scheduleName?.message}>
              <input
                id="sched-name"
                type="text"
                className="input"
                placeholder="Nightly sales reload"
                {...register("scheduleName", { required: "Required" })}
              />
            </FormField>
            <FormField label="Connection" htmlFor="sched-conn">
              <Controller
                control={control}
                name="connectionId"
                render={({ field }) => (
                  <select
                    id="sched-conn"
                    className="input"
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const next = e.target.value ? Number(e.target.value) : null;
                      field.onChange(next);
                      setValue("appId", "");
                      setValue("appName", "");
                    }}
                  >
                    <option value="">Choose connection…</option>
                    {connections.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.connectionName}
                      </option>
                    ))}
                  </select>
                )}
              />
            </FormField>
            <FormField label="App" htmlFor="sched-app" className="sm:col-span-2">
              <Controller
                control={control}
                name="appId"
                render={({ field }) => (
                  <select
                    id="sched-app"
                    className="input"
                    disabled={connectionId == null || appsQuery.isLoading}
                    value={field.value}
                    onChange={(e) => {
                      const id = e.target.value;
                      field.onChange(id);
                      const matched = appOptions.find((a) => a.appId === id);
                      setValue("appName", matched?.name ?? "");
                    }}
                  >
                    <option value="">
                      {connectionId == null
                        ? "Pick a connection first"
                        : appsQuery.isLoading
                          ? "Loading apps…"
                          : "Choose app…"}
                    </option>
                    {appOptions.map((a) => (
                      <option key={a.appId} value={a.appId}>
                        {a.name}
                        {a.spaceName ? ` (${a.spaceName})` : ""}
                      </option>
                    ))}
                  </select>
                )}
              />
            </FormField>
          </div>
        </Section>

        {/* Section 2: Frequency */}
        <Section title="Frequency" subtitle="How often should this schedule fire?">
          <Controller
            control={control}
            name="frequencyChoice"
            render={({ field }) => (
              <div
                role="radiogroup"
                aria-label="Frequency"
                className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6"
              >
                {FREQUENCY_CARDS.map((card) => {
                  const isOn = field.value === card.key;
                  return (
                    <button
                      key={card.key}
                      type="button"
                      role="radio"
                      aria-checked={isOn}
                      onClick={() => field.onChange(card.key)}
                      className={[
                        "flex flex-col items-start gap-1 rounded-card border p-3 text-left transition",
                        isOn
                          ? "border-blue bg-blue-light"
                          : "border-line bg-white card-hover",
                      ].join(" ")}
                    >
                      <span
                        aria-hidden="true"
                        className={
                          "text-lg leading-none " +
                          (isOn ? "text-blue-dark" : "text-ink-soft")
                        }
                      >
                        {card.glyph}
                      </span>
                      <span
                        className={
                          "text-sm font-semibold " +
                          (isOn ? "text-blue-dark" : "text-ink")
                        }
                      >
                        {card.title}
                      </span>
                      <span
                        className={
                          "text-[11px] " +
                          (isOn ? "text-blue-dark/80" : "text-ink-soft")
                        }
                      >
                        {card.subtitle}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          />

          {/* --- Per-frequency reveal blocks ----------------------------- */}

          {frequencyChoice === "CUSTOM" ? (
            <div className="mt-4">
              <FormField label="Cron expression" htmlFor="sched-cron">
                <input
                  id="sched-cron"
                  type="text"
                  className="input"
                  placeholder="0 3 * * *"
                  {...register("cronExpression")}
                />
              </FormField>
              {cronPreview ? (
                <p className="mt-2 text-sm italic text-ink-soft">{cronPreview}</p>
              ) : null}
            </div>
          ) : null}

          {frequencyChoice === "DAILY" ? (
            <div className="mt-4 space-y-3">
              <Controller
                control={control}
                name="dailyRunMode"
                render={({ field }) => (
                  <div role="radiogroup" aria-label="Run mode" className="flex flex-wrap gap-2">
                    {(
                      [
                        { key: "ONCE_A_DAY", label: "Once a day at start time" },
                        { key: "REPEAT", label: "Repeat throughout day" },
                      ] as const
                    ).map((opt) => {
                      const isOn = field.value === opt.key;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          role="radio"
                          aria-checked={isOn}
                          onClick={() => field.onChange(opt.key)}
                          className={[
                            "rounded-card border px-3 py-2 text-sm transition",
                            isOn
                              ? "border-blue bg-blue-light text-blue-dark font-semibold"
                              : "border-line bg-white text-ink hover:bg-bg",
                          ].join(" ")}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              />
              {dailyRunMode === "REPEAT" ? (
                <div className="space-y-1">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <FormField label="Every" htmlFor="sched-intraday-every">
                      <select
                        id="sched-intraday-every"
                        className="input"
                        {...register("intradayIntervalMinutes", { valueAsNumber: true })}
                      >
                        {INTRADAY_INTERVAL_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n} minutes
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="From" htmlFor="sched-intraday-from">
                      <select
                        id="sched-intraday-from"
                        className="input"
                        {...register("intradayStartHour", { valueAsNumber: true })}
                      >
                        {hours().map((h) => (
                          <option key={h} value={h}>
                            {formatHour(h)}
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="To" htmlFor="sched-intraday-to">
                      <select
                        id="sched-intraday-to"
                        className="input"
                        {...register("intradayEndHour", { valueAsNumber: true })}
                      >
                        {hours().map((h) => (
                          <option key={h} value={h}>
                            {formatHour(h)}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>
                  <p className="text-xs text-ink-soft">
                    e.g. every 15 min, 06:00–17:00
                  </p>
                </div>
              ) : null}
              <Controller
                control={control}
                name="weeklyDaysOfWeek"
                render={({ field }) => (
                  <DayOfWeekChips
                    value={field.value}
                    onChange={field.onChange}
                    helper="Empty = every day."
                    showWeekdaysPreset
                  />
                )}
              />
            </div>
          ) : null}

          {frequencyChoice === "WEEKLY" ? (
            <div className="mt-4 space-y-3">
              <Controller
                control={control}
                name="weeklyDaysOfWeek"
                render={({ field }) => (
                  <DayOfWeekChips
                    value={field.value}
                    onChange={field.onChange}
                    helper="Pick at least one day. Time-of-day comes from the start-time field."
                    showWeekdaysPreset
                  />
                )}
              />
            </div>
          ) : null}

          {frequencyChoice === "MONTHLY" ? (
            <div className="mt-4 space-y-3">
              <Controller
                control={control}
                name="monthlyMode"
                render={({ field }) => (
                  <div role="radiogroup" aria-label="Monthly mode" className="space-y-2">
                    {(
                      [
                        { key: "DAY_OF_MONTH", label: "Day of month" },
                        { key: "LAST_DAY", label: "Last day of month" },
                        { key: "DAYS_BEFORE_END", label: "N days before end of month" },
                        { key: "NTH_WEEKDAY", label: "Nth weekday" },
                      ] as const
                    ).map((opt) => {
                      const isOn = field.value === opt.key;
                      return (
                        <label
                          key={opt.key}
                          className={[
                            "flex flex-wrap items-center gap-3 rounded-card border px-3 py-2 text-sm transition",
                            isOn
                              ? "border-blue bg-blue-light"
                              : "border-line bg-white hover:bg-bg",
                          ].join(" ")}
                        >
                          <span className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="monthlyMode"
                              value={opt.key}
                              checked={isOn}
                              onChange={() => field.onChange(opt.key)}
                              className="h-4 w-4 border-line text-blue focus:ring-blue"
                            />
                            <span className={isOn ? "font-semibold text-blue-dark" : "text-ink"}>
                              {opt.label}
                            </span>
                          </span>
                          {opt.key === "DAY_OF_MONTH" && isOn ? (
                            <span className="flex items-center gap-2 text-xs text-ink-soft">
                              on day
                              <input
                                type="number"
                                min={1}
                                max={31}
                                className="input w-20"
                                {...register("monthlyDayOfMonth", { valueAsNumber: true })}
                              />
                            </span>
                          ) : null}
                          {opt.key === "DAYS_BEFORE_END" && isOn ? (
                            <span className="flex items-center gap-2 text-xs text-ink-soft">
                              <input
                                type="number"
                                min={0}
                                max={30}
                                className="input w-20"
                                {...register("monthlyDaysBeforeEnd", { valueAsNumber: true })}
                              />
                              days before end (0 = last day)
                            </span>
                          ) : null}
                          {opt.key === "NTH_WEEKDAY" && isOn ? (
                            <span className="flex flex-wrap items-center gap-2 text-xs text-ink-soft">
                              <select
                                className="input w-28"
                                {...register("monthlyNth", { valueAsNumber: true })}
                              >
                                {NTH_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="input w-32"
                                {...register("monthlyWeekday", { valueAsNumber: true })}
                              >
                                {/* Sunday-first list matches the schema's 0=Sun convention. */}
                                {[
                                  { v: 0, l: "Sunday" },
                                  { v: 1, l: "Monday" },
                                  { v: 2, l: "Tuesday" },
                                  { v: 3, l: "Wednesday" },
                                  { v: 4, l: "Thursday" },
                                  { v: 5, l: "Friday" },
                                  { v: 6, l: "Saturday" },
                                ].map((o) => (
                                  <option key={o.v} value={o.v}>
                                    {o.l}
                                  </option>
                                ))}
                              </select>
                            </span>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                )}
              />
              {monthlyMode === "NTH_WEEKDAY" ? (
                <p className="text-xs text-ink-soft">
                  e.g. &lsquo;Last Friday&rsquo; = Last + Friday
                </p>
              ) : null}
            </div>
          ) : null}

          {frequencyChoice === "CHAINED" ? (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField label="Parent connection" htmlFor="sched-chain-conn">
                  <Controller
                    control={control}
                    name="chainAfterConnectionId"
                    render={({ field }) => (
                      <select
                        id="sched-chain-conn"
                        className="input"
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const next = e.target.value ? Number(e.target.value) : null;
                          field.onChange(next);
                          // Reset parent app when parent connection changes.
                          setValue("chainAfterAppId", "");
                          setValue("chainAfterAppName", "");
                        }}
                      >
                        <option value="">Choose connection…</option>
                        {connections.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.connectionName}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </FormField>
                <FormField label="Parent app" htmlFor="sched-chain-app">
                  <Controller
                    control={control}
                    name="chainAfterAppId"
                    render={({ field }) => (
                      <select
                        id="sched-chain-app"
                        className="input"
                        disabled={
                          chainAfterConnectionId == null || parentAppsQuery.isLoading
                        }
                        value={field.value}
                        onChange={(e) => {
                          const id = e.target.value;
                          field.onChange(id);
                          const matched = parentAppOptions.find((a) => a.appId === id);
                          setValue("chainAfterAppName", matched?.name ?? "");
                        }}
                      >
                        <option value="">
                          {chainAfterConnectionId == null
                            ? "Pick a parent connection first"
                            : parentAppsQuery.isLoading
                              ? "Loading apps…"
                              : "Choose app…"}
                        </option>
                        {parentAppOptions.map((a) => (
                          <option key={a.appId} value={a.appId}>
                            {a.name}
                            {a.spaceName ? ` (${a.spaceName})` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </FormField>
              </div>
              {chainAfterAppName ? (
                <p className="text-xs text-ink-soft">
                  This schedule fires automatically when{" "}
                  <span className="font-semibold text-ink">{chainAfterAppName}</span>{" "}
                  reloads successfully.
                </p>
              ) : (
                <p className="text-xs text-ink-soft">
                  Pick the parent app whose successful reload triggers this schedule.
                </p>
              )}
            </div>
          ) : null}
        </Section>

        {/* Section 3: Timing — hidden for CHAINED (event-driven, no clock). */}
        {!isChained ? (
          <Section title="Timing" subtitle="When does it start, when does it stop, and in which timezone?">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <FormField label="Start" htmlFor="sched-start">
                <input
                  id="sched-start"
                  type="datetime-local"
                  className="input"
                  {...register("startTime")}
                />
              </FormField>
              <FormField label="End date (optional)" htmlFor="sched-end">
                <input
                  id="sched-end"
                  type="datetime-local"
                  className="input"
                  {...register("endDate")}
                />
              </FormField>
              <FormField label="Timezone" htmlFor="sched-tz">
                <select id="sched-tz" className="input" {...register("timezone")}>
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
          </Section>
        ) : null}

        {/* Section 4: Options */}
        <Section title="Options" subtitle="Reload type and retry policy.">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              {...register("partialReload")}
              className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
            />
            Partial reload (only reload tables marked as partial)
          </label>
          <div className="mt-4 rounded-card border border-line p-4">
            <button
              type="button"
              onClick={() => setShowRetry((v) => !v)}
              aria-expanded={showRetry}
              className="flex w-full items-center justify-between text-sm font-semibold text-ink"
            >
              <span>Retry policy</span>
              <span aria-hidden="true" className="text-ink-soft">
                {showRetry ? "−" : "+"}
              </span>
            </button>
            {showRetry ? (
              <div className="mt-4 space-y-4">
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    {...register("retryEnabled")}
                    className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
                  />
                  Retry on failure
                </label>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <FormField label="Max attempts" htmlFor="ret-max">
                    <input
                      id="ret-max"
                      type="number"
                      min={1}
                      max={20}
                      className="input"
                      {...register("maxRetryAttempts", { valueAsNumber: true })}
                    />
                  </FormField>
                  <FormField label="Delay (minutes)" htmlFor="ret-delay">
                    <input
                      id="ret-delay"
                      type="number"
                      min={1}
                      max={1440}
                      className="input"
                      {...register("retryDelayMinutes", { valueAsNumber: true })}
                    />
                  </FormField>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        {...register("exponentialBackoff")}
                        className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
                      />
                      Exponential backoff
                    </label>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </Section>

        {/* Section 5: Advanced */}
        <Section
          title="Advanced"
          subtitle="Per-schedule overrides. Leave blank to use the connection's API key."
        >
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            aria-expanded={showAdvanced}
            className="text-[12px] font-semibold uppercase tracking-[0.08em] text-blue underline-offset-2 hover:underline"
          >
            {showAdvanced ? "Hide advanced settings" : "Show advanced settings"}
          </button>
          {showAdvanced ? (
            <div className="mt-4">
              <FormField label="API key override" htmlFor="sched-key">
                <div className="flex gap-2">
                  <input
                    id="sched-key"
                    type={showApiKey ? "text" : "password"}
                    autoComplete="off"
                    className="input flex-1"
                    placeholder="Optional — overrides the connection key for this schedule"
                    {...register("apiKey")}
                  />
                  <Button
                    variant="ghost-sm"
                    onClick={() => setShowApiKey((v) => !v)}
                    aria-pressed={showApiKey}
                  >
                    {showApiKey ? "Hide" : "Show"}
                  </Button>
                </div>
              </FormField>
            </div>
          ) : null}
        </Section>

        {submitError ? <ErrorAlert>{submitError}</ErrorAlert> : null}

        <div className="flex justify-end gap-3 border-t border-line pt-5">
          <Button variant="ghost-sm" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary-sm"
            disabled={isSubmitting || create.isPending}
          >
            {create.isPending ? "Creating…" : "Create Schedule"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// --- helpers ---------------------------------------------------------------

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-extrabold uppercase tracking-[0.08em] text-ink">
          {title}
        </h3>
        {subtitle ? <p className="mt-1 text-xs text-ink-soft">{subtitle}</p> : null}
      </div>
      {children}
    </section>
  );
}

function FormField({
  label,
  htmlFor,
  error,
  children,
  className = "",
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="field-label mb-1.5 block">
        {label}
      </label>
      {children}
      {error ? (
        <p role="alert" className="mt-1 text-xs text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// Reusable Mon–Sun chip toggle. Empty selection has caller-controlled meaning
// (Daily card: "every day"; Weekly card: "pick at least one"). Reads as a
// pressed-button group for assistive tech.
function DayOfWeekChips({
  value,
  onChange,
  helper,
  showWeekdaysPreset,
}: {
  value: number[];
  onChange: (next: number[]) => void;
  helper?: string;
  showWeekdaysPreset?: boolean;
}) {
  const selected = new Set(value ?? []);
  function toggle(day: number) {
    const next = new Set(selected);
    if (next.has(day)) next.delete(day);
    else next.add(day);
    onChange(Array.from(next).sort());
  }
  return (
    <div>
      <div className="field-label mb-1.5 block">Days of week</div>
      <div className="flex flex-wrap items-center gap-2">
        {DOW_LABELS.map((d) => {
          const isOn = selected.has(d.value);
          return (
            <button
              key={d.value}
              type="button"
              aria-pressed={isOn}
              onClick={() => toggle(d.value)}
              className={[
                "chip transition",
                isOn
                  ? "bg-blue-light text-blue-dark"
                  : "bg-bg text-ink-soft hover:bg-line",
              ].join(" ")}
            >
              {d.label}
            </button>
          );
        })}
        {showWeekdaysPreset ? (
          <button
            type="button"
            onClick={() => onChange([1, 2, 3, 4, 5])}
            className="text-[11px] font-semibold uppercase tracking-[0.08em] text-blue hover:text-blue-dark"
          >
            Weekdays only
          </button>
        ) : null}
        {value && value.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft hover:text-ink"
          >
            Clear
          </button>
        ) : null}
      </div>
      {helper ? <p className="mt-1 text-xs text-ink-soft">{helper}</p> : null}
    </div>
  );
}

// Build a cron expression mirroring the start datetime for daily/weekly/
// monthly choices. Server will canonicalise via cron-parser; this is just the
// best-effort default that survives.
function cronFromStart(localDt: string, freq: "DAILY" | "WEEKLY" | "MONTHLY"): string {
  const d = new Date(localDt);
  if (Number.isNaN(d.getTime())) {
    // Fall back to midnight UTC if user hasn't picked one yet.
    return freq === "WEEKLY"
      ? "0 0 * * 0"
      : freq === "MONTHLY"
        ? "0 0 1 * *"
        : "0 0 * * *";
  }
  const m = d.getMinutes();
  const h = d.getHours();
  const dom = d.getDate();
  const dow = d.getDay();
  switch (freq) {
    case "DAILY":
      return `${m} ${h} * * *`;
    case "WEEKLY":
      return `${m} ${h} * * ${dow}`;
    case "MONTHLY":
      return `${m} ${h} ${dom} * *`;
  }
}

function toLocalDatetimeValue(d: Date): string {
  // datetime-local wants YYYY-MM-DDTHH:mm in local time.
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function localDatetimeToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function hours(): number[] {
  return Array.from({ length: 24 }, (_, i) => i);
}

function formatHour(h: number): string {
  return `${String(h).padStart(2, "0")}:00`;
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
